import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import Button from './Button';
import { todayStr } from '../../utils/expiry';

interface ExtendExpiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Context line, e.g. the student name + course/exam title. */
  subject?: string;
  currentExpiresAt?: string | null;
  /** Persist the new value; `null` clears the expiry (perpetual access). */
  onSubmit: (expiresAt: string | null) => Promise<void>;
}

function addDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Set / extend / clear a per-student access expiry (`YYYY-MM-DD`).
 * Reused for course enrollments and exam access by admins and trainers.
 */
export default function ExtendExpiryModal({
  isOpen,
  onClose,
  subject,
  currentExpiresAt,
  onSubmit,
}: ExtendExpiryModalProps) {
  const { t } = useTranslation('common');
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setDate(currentExpiresAt || '');
      setError('');
    }
  }, [isOpen, currentExpiresAt]);

  const today = todayStr();

  const handleSave = async (value: string | null) => {
    if (value && value < today) {
      setError(t('expiry.errorPast'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSubmit(value && value !== '' ? value : null);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || t('expiry.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('expiry.extendTitle')} size="sm">
      <div className="space-y-4">
        {subject && <p className="text-sm text-gray-500">{subject}</p>}

        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">{t('expiry.dateLabel')}</label>
          <input
            type="date"
            min={today}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <p className="mt-1 text-xs text-gray-400">{t('expiry.dateHint')}</p>
        </div>

        {/* Quick presets relative to today */}
        <div className="flex flex-wrap gap-2">
          {[30, 90, 180, 365].map((days) => (
            <button
              key={days}
              type="button"
              onClick={() => setDate(addDays(days))}
              className="px-2.5 py-1 text-xs rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
            >
              {t('expiry.presetDays', { days })}
            </button>
          ))}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex items-center justify-between pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => handleSave(null)}
            disabled={saving}
          >
            {t('expiry.clearPerpetual')}
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
              {t('expiry.cancel')}
            </Button>
            <Button type="button" onClick={() => handleSave(date)} isLoading={saving}>
              {t('expiry.save')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
